import { useMemo } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, Scan, Store } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";

export default function AdminStoreQR() {
  const redeemUrl = useMemo(() => {
    const base = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    return `${base}/admin/redeem`;
  }, []);

  return (
    <div className="bg-white min-h-screen text-[#09090B] print:bg-white">
      <div className="print:hidden">
        <SiteHeader />
      </div>

      <section className="border-b-2 border-[#09090B] bg-[#09090B] text-white print:hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="ws-label text-[#FFCC00]">/ Company Store</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 leading-[0.95]">
              REDEEM-DESK POSTER
            </h1>
            <p className="mt-3 text-[#A1A1AA] max-w-xl text-sm">
              Print this and tape it next to the till. Cashiers scan to land directly in the redeem flow —
              no URL to remember, no shared logins on their phone.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              data-testid="back-to-admin-link"
              className="inline-flex items-center gap-2 bg-white text-[#09090B] ws-label px-4 py-2 border-2 border-white hover:bg-[#FFCC00] transition-colors"
            >
              <ArrowLeft strokeWidth={2.5} className="w-4 h-4" /> Back
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="print-poster-btn"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white ws-label px-4 py-2 border-2 border-[#FF5A00] hover:bg-[#E65100] transition-colors"
            >
              <Printer strokeWidth={2.5} className="w-4 h-4" /> Print Poster
            </button>
          </div>
        </div>
      </section>

      {/* Poster */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-12 print:px-0 print:py-0">
        <article
          data-testid="store-poster"
          className="border-2 border-[#09090B] bg-white ws-hard-shadow print:shadow-none print:border-2 overflow-hidden"
        >
          {/* Top band */}
          <div className="bg-[#FFCC00] border-b-2 border-[#09090B] px-10 py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#09090B] text-white flex items-center justify-center">
                <Store strokeWidth={2.5} className="w-5 h-5" />
              </div>
              <div>
                <div className="ws-label">Company Store</div>
                <div className="font-display font-black text-2xl tracking-tighter">Safety Token Desk</div>
              </div>
            </div>
            <div className="font-display font-black text-5xl tracking-tighter">★</div>
          </div>

          {/* Body */}
          <div className="px-10 py-12 grid sm:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <div className="ws-label text-[#FF5A00]">For Cashiers</div>
              <h2 className="font-display font-black text-5xl sm:text-6xl tracking-tighter mt-3 leading-[0.95]">
                SCAN.<br />
                ENTER CODE.<br />
                REDEEM.
              </h2>
              <p className="mt-5 text-[#52525B] leading-relaxed max-w-sm">
                Open the camera on your iPad. Scan the QR. Enter the
                <span className="font-mono font-bold text-[#09090B]"> SAFE-XXXX-XXXX </span>
                code from the employee&apos;s ticket. Tap <strong>Mark as Redeemed</strong>.
              </p>
              <ol className="mt-6 space-y-2 text-sm">
                {[
                  { n: "01", t: "Sign in once per shift" },
                  { n: "02", t: "Look up the employee's token code" },
                  { n: "03", t: "Confirm photo matches the person" },
                  { n: "04", t: "Mark as redeemed — hand them the reward" },
                ].map((row) => (
                  <li key={row.n} className="flex gap-3 items-start">
                    <span className="ws-label text-[#FF5A00] mt-0.5">{row.n}</span>
                    <span className="text-[#09090B]">{row.t}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-[#F4F4F5] border-2 border-[#09090B] p-6 flex flex-col items-center">
              <QRCodeSVG
                data-testid="store-qr"
                value={redeemUrl}
                size={220}
                level="M"
                bgColor="#F4F4F5"
                fgColor="#09090B"
                includeMargin={false}
              />
              <div className="mt-4 flex items-center gap-2 ws-label text-[#09090B]">
                <Scan strokeWidth={2.5} className="w-4 h-4" /> Scan to open
              </div>
              <div className="mt-1 font-mono text-xs text-[#52525B] break-all text-center max-w-[220px]">
                {redeemUrl}
              </div>
            </div>
          </div>

          {/* Bottom band */}
          <div className="bg-[#09090B] text-white border-t-2 border-[#09090B] px-10 py-5 flex items-center justify-between flex-wrap gap-3">
            <div className="ws-label text-[#FFCC00]">One token · One redemption · Zero exceptions</div>
            <div className="ws-label text-[#A1A1AA]">worksafestory.com · From WorkSafeAgent LLC</div>
          </div>
        </article>
      </section>
    </div>
  );
}
